import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { recordStudentDropout, reverseStudentDropout } from '../../api/students';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Record (or reverse) an official dropout / withdrawal — the only thing,
 * besides a withdrawn application, that the Dropout Analysis counts. */
export const StudentDropoutDialog = ({
  studentId,
  mode,
  onClose,
  onDone,
}: {
  studentId: number;
  mode: 'record' | 'reverse' | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setDate(todayIso());
    setReason('');
    setError(null);
  }, [mode]);
  const mutation = useMutation({
    mutationFn: () =>
      mode === 'record'
        ? recordStudentDropout(studentId, { droppedOutAt: new Date(`${date}T12:00:00`).toISOString(), reason })
        : reverseStudentDropout(studentId, reason),
    onSuccess: onDone,
    onError: (err) => setError(isAxiosError(err) ? (err.response?.data?.message ?? 'Could not save') : 'Could not save'),
  });
  const valid = reason.trim().length >= 3 && (mode === 'reverse' || !!date);
  return (
    <Dialog open={!!mode} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'record' ? 'Record official dropout' : 'Reverse dropout'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {mode === 'record'
              ? 'Only record a dropout or withdrawal the student has confirmed. Unpaid fees or missing documents are not a dropout. This appears in the Dropout Analysis and on the Timeline.'
              : 'The student will no longer count as a dropout. The original record stays on the Timeline.'}
          </Typography>
          {mode === 'record' && (
            <TextField
              type="date"
              label="Dropped out on"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIso() } }}
            />
          )}
          <TextField
            label={mode === 'record' ? 'Reason' : 'Why is it being reversed?'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={mode === 'record' ? 'error' : 'primary'} disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
          {mode === 'record' ? 'Record dropout' : 'Reverse dropout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
